import { useState, useRef, useCallback } from 'react';

export interface TextToSpeechOptions {
  /** Default language tag, e.g. 'en-US', 'fil-PH'. Used when speak() is called without a lang. */
  lang?: string;
  /** Speaking rate (0.1 – 10, default 1). */
  rate?: number;
  /** Speaking pitch (0 – 2, default 1). */
  pitch?: number;
  /** Volume (0 – 1, default 1). */
  volume?: number;
}

export interface TextToSpeechReturn {
  /**
   * Speak the given text.
   * @param text The text to speak.
   * @param lang Optional per-call language tag (BCP-47). Overrides the hook default.
   */
  speak: (text: string, lang?: string) => void;
  /** Stop any ongoing speech immediately. */
  stop: () => void;
  /** Whether speech is currently being uttered. */
  isSpeaking: boolean;
  /** Whether the SpeechSynthesis API is supported. */
  isSupported: boolean;
  /** Pause the current utterance. */
  pause: () => void;
  /** Resume a paused utterance. */
  resume: () => void;
}

/**
 * Strip markdown syntax, HTML tags, emoji, icon references, and other
 * non-speech artifacts so only clean readable text reaches TTS.
 */
function cleanTextForSpeech(raw: string): string {
  return (
    raw
      // Remove code blocks ( ```...``` )
      .replace(/```[\s\S]*?```/g, '')
      // Remove inline code (`code`)
      .replace(/`[^`]*`/g, '')
      // Remove image markup ![alt](url)
      .replace(/!\[.*?\]\(.*?\)/g, '')
      // Remove links but keep the link text [text](url) → text
      .replace(/\[([^\]]*)\]\(.*?\)/g, '$1')
      // Remove HTML tags (including React icon elements like <i className="ri-xxx">)
      .replace(/<[^>]*>/g, '')
      // Remove emoji and other pictographic symbols
      .replace(
        /[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{2300}-\u{23FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}]/gu,
        '',
      )
      // Remove markdown heading markers
      .replace(/^#{1,6}\s+/gm, '')
      // Remove markdown bold/italic markers
      .replace(/(\*{1,3}|_{1,3})/g, '')
      // Remove horizontal rules
      .replace(/^-{3,}\s*$/gm, '')
      // Remove blockquote markers
      .replace(/^>\s+/gm, '')
      // Remove list markers
      .replace(/^[\s]*[-*+]\s+/gm, '')
      .replace(/^\s*\d+\.\s+/gm, '')
      // Remove table separators (|---|)
      .replace(/^\s*\|[-:| ]+\|\s*$/gm, '')
      // Remove standalone pipe characters (table borders)
      .replace(/\|/g, '')
      // Collapse multiple whitespace into single space
      .replace(/\s+/g, ' ')
      .trim()
  );
}

export function useTextToSpeech(options: TextToSpeechOptions = {}): TextToSpeechReturn {
  const { lang: defaultLang, rate = 1, pitch = 1, volume = 1 } = options;

  const [isSpeaking, setIsSpeaking] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  const isSupported =
    typeof window !== 'undefined' && 'speechSynthesis' in window;

  /**
   * Crude language detection — checks the script ranges of the text
   * to guess a BCP-47 tag. Returns null when it cannot determine.
   */
  const detectLang = useCallback((text: string): string | null => {
    // Only inspect first 200 chars for performance
    const sample = text.slice(0, 200);

    // Arabic / Urdu / Farsi
    if (/[\u0600-\u06FF]/.test(sample)) return 'ar-SA';
    // Chinese (simplified)
    if (/[\u4E00-\u9FFF]/.test(sample)) return 'zh-CN';
    // Japanese
    if (/[\u3040-\u309F\u30A0-\u30FF]/.test(sample)) return 'ja-JP';
    // Korean
    if (/[\uAC00-\uD7AF]/.test(sample)) return 'ko-KR';
    // Russian / Cyrillic
    if (/[\u0400-\u04FF]/.test(sample)) return 'ru-RU';
    // Greek
    if (/[\u0370-\u03FF]/.test(sample)) return 'el-GR';
    // Hebrew
    if (/[\u0590-\u05FF]/.test(sample)) return 'he-IL';
    // Thai
    if (/[\u0E00-\u0E7F]/.test(sample)) return 'th-TH';
    // Devanagari (Hindi, Marathi, Nepali)
    if (/[\u0900-\u097F]/.test(sample)) return 'hi-IN';
    // Vietnamese
    if (/[\u00C0-\u024F]/.test(sample) && /[ăâđêôơư]/.test(sample)) return 'vi-VN';

    return null; // fall back to default
  }, []);

  const speak = useCallback(
    (text: string, lang?: string) => {
      if (!isSupported || !text.trim()) return;

      // Strip non-speech artifacts before speaking
      const clean = cleanTextForSpeech(text);
      if (!clean) return;

      // Cancel any ongoing speech before starting new one
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(clean);
      utterance.rate = rate;
      utterance.pitch = pitch;
      utterance.volume = volume;

      // Priority: per-call lang > auto-detect > hook default
      const detected = lang || detectLang(text) || defaultLang;
      if (detected) utterance.lang = detected;

      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);

      utteranceRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    },
    [isSupported, defaultLang, rate, pitch, volume, detectLang],
  );

  const stop = useCallback(() => {
    if (!isSupported) return;
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  }, [isSupported]);

  const pause = useCallback(() => {
    if (!isSupported) return;
    window.speechSynthesis.pause();
  }, [isSupported]);

  const resume = useCallback(() => {
    if (!isSupported) return;
    window.speechSynthesis.resume();
  }, [isSupported]);

  return { speak, stop, isSpeaking, isSupported, pause, resume };
}
